import type { CtcAttachmentNumberKey, CtcAttachmentTemplate, CtcBroker, CtcCommission, CtcCommodity, CtcCustomer, CtcPurchaseOrder, CtcStaffProfile } from "../webapp/src/types";
import { ctcParseLogo } from "./ctcReports";
import { ctcDynamicFieldRows } from "./ctcMasterData";

/**
 * The five generated "Attachment No. N" documents: built-in texts, template rules and the HTML
 * the PDF is printed from. Shared so the server PDF (functions-ctc/src/pdf.ts) and the Back office
 * preview render exactly the same page. Pure: no Firestore / Storage here.
 *
 * Legacy sources: KFH-FE/KHFBrokerage-Web/src/assets/attached-reports/*.html and
 * KFH-BE/KFH.WebAPI/Extensions/AttachmentPdfGenerator.cs (QuestPDF port of 1 and 3).
 */

export type AttachmentNumber = 1 | 2 | 3 | 4 | 5;

/**
 * Where attachment templates live: under the CTC config doc, one document per template, each with
 * its own `versions` subcollection. (Not inside the ctcTemplates doc itself: history, transactions
 * and the 1 MB document limit need one document per template.)
 */
export const CTC_ATTACHMENT_TEMPLATES_PATH = "ctc_config/ctcTemplates/templates";
export type AttachmentLanguage = "en" | "ar";

/**
 * Header logo: attachments 1 and 3 (the customer's requests) carry the customer's logo
 * (ctc_customers.logoPath); 2, 4 and 5 (CTC's confirmations) carry the CTC logo, stored as base64
 * (plain or a data URL, PNG / JPEG) in ctc_config/ctcTemplates.ctc_logo.
 * No logo: the name is printed instead (customer's name on 1 and 3, Commodity Trading Center on 2, 4, 5).
 */
export const CTC_LOGO_DOC = "ctc_config/ctcTemplates";
export const CTC_LOGO_FIELD = "ctc_logo";
export const ctcAttachmentUsesCustomerLogo = (n: AttachmentNumber) => n === 1 || n === 3;

/**
 * Who signs the customer's requests: attachment 1 = the checker who approved it (the maker when the
 * customer has no checker step); attachment 3 = whoever requested the transfer. Their name and
 * signature come from ctc_staff/{email}. Attachments 2, 4, 5 are signed by the assigned dealer.
 */
export const ctcCustomerSignerEmail = (n: AttachmentNumber, po: Pick<CtcPurchaseOrder, "makerEmail" | "checkerEmail" | "transferRequestedByEmail">): string | undefined =>
    n === 1 ? po.checkerEmail ?? po.makerEmail : n === 3 ? po.transferRequestedByEmail ?? po.checkerEmail ?? po.makerEmail : undefined;
/** The CTC logo as an <img> data URL, or undefined if missing / not a PNG or JPEG. */
export const ctcLogoDataUrl = (logo: unknown): string | undefined => {
    const image = ctcParseLogo(logo);
    return image ? `data:image/${image.extension};base64,${image.base64}` : undefined;
};

export const CTC_ATTACHMENT_NUMBERS: AttachmentNumber[] = [1, 2, 3, 4, 5];
export const CTC_ATTACHMENT_TEXT_MAX = 400;
export const CTC_ATTACHMENT_NAME_MAX = 80;

export const CTC_ATTACHMENT_TITLES: Record<AttachmentNumber, string> = {
    1: "REQUEST NEW PURCHASE ORDERS",
    2: "CONFIRM PURCHASE ORDER REQUEST",
    3: "REQUEST OWNERSHIP TRANSFER",
    4: "CONFIRM OWNERSHIP TRANSFER",
    5: "CONFIRM SELL REQUEST",
};

const AGREEMENT_AR = "إشارة إلى الموضوع أعلاه وإلى اتفاقية الوكالة المنظمة لشراء وبيع السلع والأصول في الأسواق المحلية والدولية والموقعة معكم";

/** The text each attachment had before templates; seeds each number's first default template. */
export const CTC_BUILTIN_ATTACHMENT_TEXT: Record<AttachmentNumber, { en: string; ar: string }> = {
    1: {
        ar: `السلام عليكم ورحمة الله وبركاته... ${AGREEMENT_AR}، يرجى التكرم بتنفيذ صفقة شراء سلع أو أصول نقداً لصالحنا من السوق المحلية أو الدولية حسب الموضوع أدناه.`,
        en: "With reference to the agency agreement for buying and selling commodities signed with you, kindly execute a cash purchase of commodities on our behalf as detailed above.",
    },
    2: {
        ar: `تحية طيبة وبعد... ${AGREEMENT_AR}، نحيطكم علماً بأنه تم تنفيذ صفقة الشراء حسب التفاصيل أدناه. وتفضلوا بقبول فائق الاحترام.`,
        en: "We confirm that the purchase has been executed as detailed above.",
    },
    3: {
        ar: `تحية طيبة وبعد... ${AGREEMENT_AR}، وبناءً على إتمامنا لعملية البيع بالمرابحة مع عميلنا، يرجى العمل على تحويل السلعة حسب التفاصيل المبينة أدناه. وتفضلوا بقبول فائق الاحترام.`,
        en: "Having completed the Murabaha sale with our client, kindly transfer the commodity as detailed above.",
    },
    4: {
        ar: `تحية طيبة وبعد... ${AGREEMENT_AR}، وبناءً على كتابكم بإتمام عملية تحويل السلع، نحيطكم علماً بأننا قد قمنا بتحويل السلعة المذكورة أدناه وفقاً للتفاصيل الآتية. وتفضلوا بقبول فائق الاحترام.`,
        en: "Further to your transfer request, we confirm that ownership of the commodity has been transferred as detailed above.",
    },
    5: {
        ar: `تحية طيبة وبعد... ${AGREEMENT_AR}، وبناءً على تفويض عميلكم لنا بالبيع، تم تنفيذ عملية البيع حسب التفاصيل أدناه.`,
        en: "Based on your client's authorisation to sell, the sale has been executed as detailed above.",
    },
};

/** Fixed legal clause of attachment 5 (not part of the editable text). */
const SALE_AUTHORISATION_AR = "وبموجب هذا نفوضكم بتحويل سعر البيع الإجمالي المبين أعلاه إلى حساب المستفيد لديكم خصماً من حساب الذمم الخاص بنا لديكم وتسوية الحساب فيما يخص هذه الصفقة وإشعارنا بذلك، ويقر الطرفان بسلامة وصحة إجراءات الصفقة ما لم يتم الاعتراض علينا خلال أسبوع من تاريخ تنفيذها، علماً بأن هذا التفويض خاضع لأحكام وشروط الاتفاقية الموقعة بيننا. وتفضلوا بقبول فائق الاحترام.";

export const isAttachmentNumber = (n: unknown): n is AttachmentNumber => CTC_ATTACHMENT_NUMBERS.includes(n as AttachmentNumber);

/** Why a template's content can't be saved, or undefined. Server and Back office form use the same rule. */
export const ctcAttachmentTemplateProblem = (t: { name?: unknown; text_en?: unknown; text_ar?: unknown }): string | undefined => {
    const name = typeof t.name === "string" ? t.name.trim() : "";
    const en = typeof t.text_en === "string" ? t.text_en.trim() : "";
    const ar = typeof t.text_ar === "string" ? t.text_ar.trim() : "";
    if (!name) return "Name is required";
    if (name.length > CTC_ATTACHMENT_NAME_MAX) return `Name is longer than ${CTC_ATTACHMENT_NAME_MAX} characters`;
    if (!en) return "English text is required";
    if (!ar) return "Arabic text is required";
    if (en.length > CTC_ATTACHMENT_TEXT_MAX) return `English text is longer than ${CTC_ATTACHMENT_TEXT_MAX} characters`;
    if (ar.length > CTC_ATTACHMENT_TEXT_MAX) return `Arabic text is longer than ${CTC_ATTACHMENT_TEXT_MAX} characters`;
    return undefined;
};

const usable = (t: CtcAttachmentTemplate | undefined) => !!t && t.retired !== true && t.deleted !== true;

/**
 * The template an attachment of this customer uses: the customer's choice for that number if it
 * is still active, otherwise that number's default. Undefined = built-in text.
 */
export const ctcResolveAttachmentTemplate = (
    n: AttachmentNumber,
    customer: Pick<CtcCustomer, "attachmentTemplateIds"> | undefined,
    templates: CtcAttachmentTemplate[],
): CtcAttachmentTemplate | undefined => {
    const forNumber = templates.filter((t) => t.attachmentNumber === n);
    const chosenId = customer?.attachmentTemplateIds?.[String(n) as CtcAttachmentNumberKey];
    const chosen = forNumber.find((t) => t.id === chosenId);
    if (usable(chosen)) return chosen;
    return forNumber.find((t) => t.isDefault && usable(t));
};

/** The order's language picks the attachment text; Arabic unless the beneficiary chose English. */
export const ctcAttachmentLanguage = (po: Pick<CtcPurchaseOrder, "beneficiary">): AttachmentLanguage => po.beneficiary?.language === "en" ? "en" : "ar";

export interface AttachmentRenderContext {
    po: CtcPurchaseOrder;
    customer: CtcCustomer;
    commodity?: CtcCommodity;
    broker?: CtcBroker;
    /** The order's commission profile ("Trading type"); its name is printed, never the id. */
    commission?: Pick<CtcCommission, "name_en" | "name_ar">;
    dealer?: CtcStaffProfile;
    /** Bank user who signs attachment 1 / 3 (ctcCustomerSignerEmail), with their ctc_staff profile if any. */
    customerSigner?: { email: string; profile?: CtcStaffProfile };
    /** Header logo for this attachment: the customer's (1, 3) or CTC's (2, 4, 5). */
    logoDataUrl?: string;
    /** Signature of whoever signs this attachment: the customer signer (1, 3) or the dealer (2, 4, 5). */
    signatureDataUrl?: string;
    /** Text for this attachment; omitted = built-in text. */
    text?: { en: string; ar: string };
    language: AttachmentLanguage;
    /** The customer's current dynamic fields (by id): labels for orders saved before labels were copied onto them. */
    dynamicFields?: Record<string, { name_en?: string; name_ar?: string; order?: number }>;
}

type TimestampLike = { toDate?: () => Date } | undefined;
const fmtDate = (ts?: TimestampLike) => {
    const d = ts?.toDate?.();
    return d ? d.toLocaleString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: true, timeZone: "Asia/Kuwait" }) : "";
};
const fmtMoney = (n?: number, currency?: string, decimals = 3) =>
    n === undefined || n === null ? "" : `${n.toLocaleString("en-US", { minimumFractionDigits: decimals, maximumFractionDigits: decimals })} ${currency ?? ""}`.trim();
const esc = (s: unknown) => String(s ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", "\"": "&quot;", "'": "&#39;" }[c] as string));

const CSS = `
  @page { size: A4; margin: 18mm 16mm; }
  * { box-sizing: border-box; }
  body { font-family: "Helvetica Neue", Arial, "Noto Naskh Arabic", "Tahoma", sans-serif; color: #444; font-size: 12px; margin: 0; }
  .hdr { display: flex; justify-content: space-between; align-items: flex-end; border-bottom: 1px solid #ccc; padding-bottom: 6px; }
  .hdr img { max-height: 50px; max-width: 160px; }
  .title { text-align: center; margin: 12px 0 4px; font-size: 20px; color: #666; font-weight: 600; }
  .subtitle { text-align: center; font-size: 22px; color: #00934C; font-weight: 600; margin-bottom: 14px; }
  table.kv { width: 100%; border-collapse: collapse; background: #f7f7f7; margin: 10px 0; }
  table.kv td { padding: 6px 8px; border-bottom: 1px solid #fff; vertical-align: top; }
  table.kv td.k { width: 32%; color: #666; }
  table.kv td.c { width: 3%; color: #666; }
  table.kv td.v { color: #00934C; font-weight: 600; }
  .ar { direction: rtl; text-align: right; font-size: 13px; line-height: 1.7; margin: 10px 0; }
  .en { direction: ltr; text-align: left; font-size: 12px; line-height: 1.6; margin: 8px 0; color: #555; }
  .sig { margin-top: 28px; display: flex; justify-content: flex-end; }
  .sig .box { width: 220px; text-align: center; }
  .sig img { max-height: 65px; max-width: 200px; }
  .sig .line { border-top: 1px solid #ccc; margin-top: 6px; padding-top: 4px; color: #666; }
  .foot { margin-top: 30px; font-size: 10px; color: #999; text-align: center; }
`;

const kv = (rows: Array<[string, unknown]>) =>
    `<table class="kv">${rows.filter(([, v]) => v !== undefined && v !== null && v !== "").map(([k, v]) => `<tr><td class="k">${esc(k)}</td><td class="c">:</td><td class="v">${esc(v)}</td></tr>`).join("")}</table>`;

const CTC = "Commodity Trading Center";

const page = (ctx: AttachmentRenderContext, n: AttachmentNumber, from: string, to: string, rows: Array<[string, unknown]>, signer?: string, extraAr?: string) => {
    const text = (ctx.text ?? CTC_BUILTIN_ATTACHMENT_TEXT[n])[ctx.language];
    return `
<!DOCTYPE html><html><head><meta charset="utf-8"><style>${CSS}</style></head><body>
  <div class="hdr">
    <div>${ctx.logoDataUrl ? `<img src="${ctx.logoDataUrl}" alt="logo">` : `<strong>${esc(ctcAttachmentUsesCustomerLogo(n) ? ctx.customer.name_en : CTC)}</strong>`}</div>
    <div style="text-align:right;color:#666">Request date: ${esc(fmtDate(ctx.po.created))}<br>Reference number: <strong>${esc(ctx.po.referenceNumber)}</strong></div>
  </div>
  <div class="title">Attachment No. ${n}</div>
  <div class="subtitle">${esc(CTC_ATTACHMENT_TITLES[n])}</div>
  ${kv([["From", from], ["To", to]])}
  <div class="${ctx.language}">${esc(text)}</div>
  ${kv(rows)}
  ${extraAr ? `<div class="ar">${extraAr}</div>` : ""}
  <div class="sig"><div class="box">${ctx.signatureDataUrl ? `<img src="${ctx.signatureDataUrl}" alt="signature">` : "<div style='height:65px'></div>"}<div class="line">${esc(signer ?? "")}</div></div></div>
  <div class="foot">Generated by the Commodity Trading Center · ${esc(new Date().toISOString())}</div>
</body></html>`;
};

export const buildAttachmentHtml = (n: AttachmentNumber, ctx: AttachmentRenderContext): string => {
    const { po, customer, commodity, broker } = ctx;
    const customerName = customer.name_en;
    const beneficiary = po.beneficiary;
    const qty = po.quantity !== undefined ? `${po.quantity} ${commodity?.measurementUnit ?? ""}`.trim() : undefined;
    const customerSignerEmail = ctx.customerSigner?.email ?? ctcCustomerSignerEmail(n, po);
    const signerCustomer = ctx.customerSigner?.profile?.displayName?.trim() || customerSignerEmail;
    const signerDealer = ctx.dealer?.displayName ?? po.assignedDealerEmail ?? CTC;
    // Commission profile name in the order's language (falls back to English, then the id)
    const tradingType = (ctx.language === "ar" ? ctx.commission?.name_ar || ctx.commission?.name_en : ctx.commission?.name_en) || po.commissionId;
    switch (n) {
        case 1:
            return page(ctx, 1, customerName, CTC,
                [["Amount", fmtMoney(po.amount, po.currencyId)], ["Trading type", tradingType], ["Beneficiary number", beneficiary?.number], ["Request time", fmtDate(po.submittedAt ?? po.created)], ["Client reference", po.clientRefNo], ["Mobile number", `${beneficiary?.mobile_code ?? ""} ${beneficiary?.mobile ?? ""}`], ...ctcDynamicFieldRows(po, ctx.language === "ar" ? "ar" : "en", ctx.dynamicFields)],
                signerCustomer);
        case 2:
            return page(ctx, 2, CTC, customerName,
                [["Purchase date", fmtDate(po.purchasedAt)], ["Beneficiary number", beneficiary?.number], ["Commodity", commodity?.name_en ?? po.commodityId], ["Quantity", qty], ["Location", po.locationName], ["Unit price", po.unitPrice], ["Amount", fmtMoney(po.amount, po.currencyId)], ["Commission", fmtMoney(po.customerCommission, po.commissionCurrency?.customer)], ["Broker", broker?.name_en], ["Payment details", "The amount due is recorded in our settlement account."], ["Certificate number", po.certificateNumber]],
                signerDealer);
        case 3:
            return page(ctx, 3, customerName, CTC,
                [["Transfer request date", fmtDate(po.updated)], ["Commodity", commodity?.name_en ?? po.commodityId], ["Quantity", qty], ["From account", customerName], ["To account", beneficiary?.accountNumber ?? beneficiary?.number]],
                signerCustomer);
        case 4:
            return page(ctx, 4, CTC, customerName,
                [["Transaction reference", po.referenceNumber], ["Transfer date", fmtDate(po.transferredAt)], ["From account", customerName], ["To account", beneficiary?.accountNumber ?? beneficiary?.number], ["Commodity", commodity?.name_en ?? po.commodityId], ["Quantity", qty], ["Beneficiary number", beneficiary?.number]],
                signerDealer);
        case 5:
            return page(ctx, 5, CTC, customerName,
                [["Transaction reference", po.referenceNumber], ["Sale date", fmtDate(po.soldAt)], ["Beneficiary number", beneficiary?.number], ["Commodity", commodity?.name_en ?? po.commodityId], ["Quantity", qty], ["Total price", fmtMoney(po.amount, po.currencyId)], ["Sold via", po.soldMethod]],
                signerDealer, SALE_AUTHORISATION_AR);
    }
};
